import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import {
  Package,
  ArrowRightLeft,
  AlertTriangle,
  Briefcase,
} from 'lucide-react';

interface DashboardStats {
  totalTools: number;
  activeCheckouts: number;
  lowStockAlerts: number;
  activeJobs: number;
}

export function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const res = await api.get<DashboardStats>('/reports/dashboard');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const statCards = [
    {
      label: 'Tool Types',
      value: stats?.totalTools || 0,
      icon: Package,
      color: 'text-primary',
      bgColor: 'bg-primary/20',
    },
    {
      label: 'Active Checkouts',
      value: stats?.activeCheckouts || 0,
      icon: ArrowRightLeft,
      color: 'text-warning',
      bgColor: 'bg-warning/20',
    },
    {
      label: 'Low Stock Alerts',
      value: stats?.lowStockAlerts || 0,
      icon: AlertTriangle,
      color: 'text-error',
      bgColor: 'bg-error/20',
    },
    {
      label: 'Active Jobs',
      value: stats?.activeJobs || 0,
      icon: Briefcase,
      color: 'text-success',
      bgColor: 'bg-success/20',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-foreground-muted">Overview of your tool crib</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label}>
            <CardBody className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`h-6 w-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm text-foreground-muted">{stat.label}</p>
                <p className="text-2xl font-bold">
                  {isLoading ? '-' : stat.value}
                </p>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader>Recent Activity</CardHeader>
          <CardBody>
            <div className="text-center py-8 text-foreground-muted">
              <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No recent activity</p>
            </div>
          </CardBody>
        </Card>

        {/* Low Stock Alerts */}
        <Card>
          <CardHeader>Low Stock Alerts</CardHeader>
          <CardBody>
            <div className="text-center py-8 text-foreground-muted">
              <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No alerts</p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
